import { writable, derived, get } from 'svelte/store';
import type { ContentManifest, ArticleContent, ContentItem } from '$types/content.js';

// Content manifest store
export const manifest = writable<ContentManifest | null>(null);

// Current article path (without #/ prefix)
export const currentPath = writable<string>('');

// Cache for loaded article content
const articleCache = new Map<string, ArticleContent>();

// Loading state
export const isLoading = writable(false);

// Current article content
export const currentArticle = writable<ArticleContent | null>(null);

// Error state: distinguishes a true missing article from a network/offline failure
export type ArticleLoadError = 'not-found' | 'offline';
export const articleError = writable<ArticleLoadError | null>(null);

// Load manifest from server
export async function loadManifest(): Promise<void> {
  try {
    const response = await fetch('/manifest.json');
    if (!response.ok) throw new Error('Failed to load manifest');
    const data = await response.json();
    manifest.set(data);
  } catch (error) {
    console.error('Error loading manifest:', error);
  }
}

// Get content item by path
export function getContentItem(path: string): ContentItem | undefined {
  const m = get(manifest);
  if (!m) return undefined;
  return m.byPath[path];
}

// Derived stores for volumes tree structure
export const volumeTree = derived(manifest, ($manifest) => {
  if (!$manifest) return [];

  // Group volumes by parent
  const byParent = new Map<string, typeof $manifest.volumes>();

  for (const item of $manifest.volumes) {
    const parent = item.parent || '';
    if (!byParent.has(parent)) {
      byParent.set(parent, []);
    }
    byParent.get(parent)!.push(item);
  }

  // Build tree structure
  interface TreeNode {
    item?: typeof $manifest.volumes[0];
    name: string;
    path: string;
    children: TreeNode[];
  }

  function buildTree(parentPath: string): TreeNode[] {
    const children = byParent.get(parentPath) || [];
    const result: TreeNode[] = [];

    // Get unique parent directories
    const directories = new Set<string>();
    for (const item of children) {
      if (item.parent && item.parent !== parentPath) {
        const nextLevel = item.parent.split('/').slice(0, parentPath ? parentPath.split('/').length + 1 : 1).join('/');
        directories.add(nextLevel);
      }
    }

    // Add directory nodes
    for (const dir of directories) {
      const name = dir.split('/').pop() || dir;
      result.push({
        name: name.replace(/^\d+-/, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        path: dir,
        children: buildTree(dir),
      });
    }

    // Add article nodes at this level
    for (const item of children) {
      if (item.parent === parentPath || (!item.parent && !parentPath)) {
        result.push({
          item,
          name: item.title,
          path: item.path,
          children: [],
        });
      }
    }

    return result;
  }

  return buildTree('');
});

// Get previous and next articles in volume sequence
export function getVolumeNavigation(path: string): { prev?: ContentItem; next?: ContentItem } {
  const m = get(manifest);
  if (!m) return {};

  const volumePath = path.replace(/^volumes\//, '');
  const navItems = m.volumes.filter(v => !v.noNav);
  const index = navItems.findIndex(v => v.path === volumePath);

  if (index === -1) return {};

  return {
    prev: index > 0 ? navItems[index - 1] : undefined,
    next: index < navItems.length - 1 ? navItems[index + 1] : undefined,
  };
}

// Load article content from pre-rendered JSON.
// Pass silent=true to skip the loading state (used for HMR reloads so the
// existing content stays visible and the page doesn't scroll back to the top).
export async function loadArticle(path: string, silent = false): Promise<ArticleContent | null> {
  // Clear any previous error at the start of each load attempt
  articleError.set(null);

  // Check in-memory cache (session-only; SW handles persistence across reloads)
  if (articleCache.has(path)) {
    const cached = articleCache.get(path)!;
    currentArticle.set(cached);
    return cached;
  }

  if (!silent) isLoading.set(true);

  try {
    if (!path.startsWith('volumes/') && !path.startsWith('wiki/')) {
      articleError.set('not-found');
      currentArticle.set(null);
      return null;
    }

    const response = await fetch(`/articles/${path}.json`);

    if (!response.ok) {
      articleError.set('not-found');
      currentArticle.set(null);
      return null;
    }

    const data = await response.json();

    // Get navigation for volumes
    const nav = path.startsWith('volumes/') ? getVolumeNavigation(path) : {};

    const article: ArticleContent = {
      html: data.html,
      title: data.title,
      toc: data.toc,
      prev: nav.prev ? { path: `volumes/${nav.prev.path}`, title: nav.prev.title } : undefined,
      next: nav.next ? { path: `volumes/${nav.next.path}`, title: nav.next.title } : undefined,
    };

    articleCache.set(path, article);
    currentArticle.set(article);
    return article;
  } catch (error) {
    // TypeError from fetch() means a network failure (offline, DNS, connection refused).
    // Any other error type is an unexpected problem — treat as not-found.
    if (error instanceof TypeError) {
      articleError.set('offline');
    } else {
      console.error('Error loading article:', error);
      articleError.set('not-found');
    }
    currentArticle.set(null);
    return null;
  } finally {
    if (!silent) isLoading.set(false);
  }
}

// Read from article cache without fetching
export function getCachedArticle(path: string): ArticleContent | null {
  return articleCache.get(path) ?? null;
}

// Clear article cache
export function clearCache(): void {
  articleCache.clear();
}

// Dev: reload article content in-place when the build pipeline rewrites its JSON
if (import.meta.hot) {
  import.meta.hot.on('article-updated', ({ path }: { path: string }) => {
    articleCache.delete(path);
    if (get(currentPath) === path) {
      loadArticle(path, true);
    }
  });
}
